import { useState } from "react";
import { useNavigate } from "react-router-dom";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* USER DEFINED COMPONENTS */
import LoadingButton from "components/button";
import Input from "components/input";

/* ICONS */
import { IoMdEye, IoMdEyeOff } from "react-icons/io";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* REDUX IMPORTS */
import { useDispatch } from "react-redux";
import actions from "redux/actions/userAction";

import { useFormik } from "formik";
import * as yup from "yup";

/* ASSETS */
import Logo from "assets/images/logo-dark.png";

/* API IMPORT */
import { adminLogin } from "service/api";

/* VALIDATION SCHEMA */
const loginValidation = yup.object().shape({
  email: yup
    .string()
    .email()
    .required("Email address is required"),
  password: yup
    .string()
    .min(2, "Too short")
    .required("Password is required"),
});

const AuthLogin = () => {
  // GLOBAL STATES
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // STATE VARIABLES
  const [loginPassword, setLoginPassword] = useState(false);
  const [loader, setLoader] = useState(false);

  // FORMIK LOGIN FUNCTION
  const loginHandler = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginValidation,
    onSubmit: (values) => {
      setLoader(true);
      adminLogin(values)
        .then((res) => {
          if (res?.status) {
            dispatch(actions.setToken(res?.data?.token));
            dispatch(actions.setUser(res?.data?.userData));
            dispatch(actions.setLoggedIn(true));

            toast.success(res?.message);
            loginHandler.resetForm();
            navigate("/dashboard");
          } else {
            toast.error(res?.message);
          }
        })
        .catch((e) => {
          toast.error(e?.response?.data?.message || "Unable to login");
        })
        .finally(() => {
          setLoader(false);
        });
    },
  });

  return (
    <section className="bg-authBg">
      {/* AUTH FORM */}
      <div className="h-authForm">
        <div className="container h-full flex items-center justify-center">
          <div className="w-[60vh] bg-white rounded-lg border border-authBorder p-10">
            <img src={Logo} className="w-40 object-contain" alt="WellCare Pharmacy" />
            <div className="mt-5">
              <h1 className="text-xl font-ibm-bold text-black">
                Admin Panel Login
              </h1>
              <h2 className="mt-1 text-base font-ibm-regular text-greyText">
                Manage your website's services, contact details, and the
                requests submitted by your patients.
              </h2>
            </div>

            <form onSubmit={loginHandler.handleSubmit}>
              {/* EMAIL ADDRESS */}
              <div className="form-group mt-5">
                <label className="text-base font-ibm-regular text-black">
                  Email address <span className="text-red-500">*</span>
                  <div className="mt-1">
                    <Input
                      type="text"
                      name="email"
                      id="email"
                      placeholder="Email Address"
                      value={loginHandler.values.email}
                      onChange={loginHandler.handleChange}
                      error={
                        loginHandler.errors.email && loginHandler.touched.email
                      }
                    />
                  </div>
                </label>
              </div>

              {/* PASSWORD */}
              <div className="form-group mt-5">
                <label className="text-base font-ibm-regular text-black">
                  Password <span className="text-red-500">*</span>
                  <div className="mt-1 relative">
                    <Input
                      type={loginPassword ? "text" : "password"}
                      name="password"
                      id="password"
                      placeholder="Password"
                      value={loginHandler.values.password}
                      onChange={loginHandler.handleChange}
                      error={
                        loginHandler.errors.password &&
                        loginHandler.touched.password
                      }
                    />
                    <Button
                      className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5 !absolute top-2 right-2"
                      onClick={() => setLoginPassword(!loginPassword)}
                    >
                      {loginPassword ? (
                        <IoMdEyeOff className="w-5 h-5 text-greyText" />
                      ) : (
                        <IoMdEye className="w-5 h-5 text-greyText" />
                      )}
                    </Button>
                  </div>
                </label>
              </div>

              <LoadingButton type="submit" label="Login" loading={loader} />
            </form>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="py-3 border-t border-authBorder bg-white">
        <div className="container">
          <div className="flex items-center justify-between">
            <p className="text-base font-ibm-regular text-black">
              Copyright {new Date().getFullYear()} © WellCare Pharmacy. All
              rights reserved.
            </p>
            <p className="text-base font-ibm-regular text-black">
              Designed by{" "}
              <a
                href="https://www.roundtechsquare.com"
                target="_blank"
                rel="noreferrer"
                className="text-sm font-ibm-semibold text-blue-500"
              >
                RoundTechSquare
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AuthLogin;
